// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { StyledButton } from "@insite/mobius/Clickable";
import DataTableCellBase, { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { injectCss } from "@insite/mobius/utilities";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import DataTableContext from "@vanmeter/MobiusOverrides/DataTable/DataTableContext";
import { fontConfig } from "@vanmeter/XDL/constants";
import { ellipsis, xScreen } from "@vanmeter/XDL/Utilities";
import * as React from "react";
import styled, { css, withTheme } from "styled-components";

export interface DataTableCellProps extends DataTableCellBaseProps {
    /** Props to be passed to the cell's typography child. */
    typographyProps?: TypographyPresentationProps;
    /**
     * Indicates how the `css` property is combined with the default `css` property from the theme.
     * If true, the default css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the default css in the theme.
     */
    mergeCss?: boolean;
    // XNG-Changes
    header?: string;
}

// XNG-Changes
const MobileDataTableCellBase = styled.div`
    ${injectCss}
    display: flex;
    justify-content: flex-start;
    align-items: start;
    column-gap: 16px;
    > ${StyledButton} {
        display: block;
        ${ellipsis(2)}
        ${TypographyStyle} {
            font-size: ${fontConfig.smallSingleLine.size}px;
            line-height: ${fontConfig.smallSingleLine.lineHeight};
            font-weight: ${fontConfig.weight.bold};
        }
    }

    padding: 0;
    max-width: unset;
    width: 100%;
`;

// XNG-Changes
const HeaderTypography = styled.p`
    flex: 0 0 calc(50% - 8px);
    font-size: ${fontConfig.smallSingleLine.size}px;
    line-height: ${fontConfig.smallSingleLine.lineHeight};
    font-weight: ${fontConfig.weight.bold};
    white-space: nowrap;
    text-align: left;
`;

// XNG-Changes
const ValueTypography = styled.p`
    flex: 0 0 calc(50% - 8px);
    font-size: ${fontConfig.smallSingleLine.size}px;
    line-height: ${fontConfig.smallSingleLine.lineHeight};
    ${ellipsis(2)}
    &:empty:after {
        content: "- -";
    }
`;

const DataTableCell: React.FC<DataTableCellProps> = ({
    mergeCss,
    css: cellCss = "",
    children,
    typographyProps,
    ...otherProps
}) => {
    const { applyStyledProp } = applyPropBuilder({ ...otherProps }, { component: "dataTable" });
    const { theme } = otherProps;
    const resolvedMergeCss = mergeCss ?? theme?.dataTable.defaultProps?.mergeCss;
    // XNG-Changes: add MobileDataTableCellBase
    return (
        <DataTableContext.Consumer>
            {({ cellTypographyProps, isMobile }) =>
                isMobile ? (
                    <MobileDataTableCellBase
                        {...otherProps}
                        css={css`
                            ${applyStyledProp(["cssOverrides", "cell"], resolvedMergeCss)}
                            ${cellCss}
                        `}
                    >
                        <HeaderTypography>{otherProps.header}</HeaderTypography>
                        {typeof children === "string" ? (
                            <ValueTypography {...cellTypographyProps} {...typographyProps}>
                                {children}
                            </ValueTypography>
                        ) : (
                            children
                        )}
                    </MobileDataTableCellBase>
                ) : (
                    <DataTableCellBase
                        {...otherProps}
                        css={css`
                            ${applyStyledProp(["cssOverrides", "cell"], resolvedMergeCss)}
                            ${cellCss}
                        `}
                    >
                        {typeof children === "string" || typeof children === "number" ? (
                            <Typography {...cellTypographyProps} {...typographyProps}>
                                {children}
                            </Typography>
                        ) : (
                            children
                        )}
                    </DataTableCellBase>
                )
            }
        </DataTableContext.Consumer>
    );
};

export default withTheme(DataTableCell);
